// Business dates are CALENDAR dates, and this module is the reason they stay
// that way on screen.
//
// §06: business dates are `YYYY-MM-DD` with no timezone — an invoice due
// 2026-09-01 is due on that calendar day in Vancouver and in Berlin. The
// obvious rendering, `new Date("2026-09-01").toLocaleDateString()`, parses the
// string as UTC MIDNIGHT and then prints it in the viewer's zone, so anyone
// west of UTC reads "Aug 31, 2026" — a due date reported one day early, every
// time, for the whole team. Every date here is therefore parsed into plain
// year/month/day numbers and formatted with an explicit UTC timezone, which
// makes the local zone irrelevant by construction.
//
// Audit instants (`generated_at`, `created_at`) are the opposite case: they are
// RFC 3339 UTC points in time, and showing them in the reader's local zone is
// correct. `formatInstant` is the only function here that touches local time.

const LOCALE = "en-CA";

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const CALENDAR_MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

export interface CalendarDate {
  year: number;
  /** 1-12. Not the 0-indexed month `Date` uses — that off-by-one is a bug farm. */
  month: number;
  day: number;
}

/** Parse `YYYY-MM-DD`. Returns null for anything else rather than guessing. */
export function parseCalendarDate(iso: string): CalendarDate | null {
  const match = CALENDAR_DATE_PATTERN.exec(iso);
  if (match === null) return null;
  const [, year, month, day] = match;
  if (year === undefined || month === undefined || day === undefined) return null;
  return { year: Number(year), month: Number(month), day: Number(day) };
}

/**
 * A `Date` positioned at UTC midnight of the given calendar day. Only ever
 * handed to an `Intl` formatter pinned to `timeZone: "UTC"`, so the instant is
 * an implementation detail and never leaks into a local-time rendering.
 */
function atUtcMidnight(date: CalendarDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day));
}

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  year: "numeric",
  month: "short",
  day: "numeric",
});

const shortDateFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});

const monthFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  year: "numeric",
  month: "short",
});

const longMonthFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  year: "numeric",
  month: "long",
});

const weekdayShortFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  weekday: "short",
});

const weekdayLongFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  weekday: "long",
});

const instantFormatter = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: "medium",
  timeStyle: "short",
});

/** `"2026-09-01"` -> `"Sep 1, 2026"`, in every timezone on earth. */
export function formatCalendarDate(iso: string): string {
  const parsed = parseCalendarDate(iso);
  if (parsed === null) return iso;
  return dateFormatter.format(atUtcMidnight(parsed));
}

/**
 * `"2026-09-15"` -> `"Tuesday, Sep 15, 2026"`. The heading of a screen that is
 * about ONE day: the weekday is the thing a person checks a due date against
 * ("that's a Saturday"), and it is exactly what a bare date hides.
 */
export function formatCalendarWeekdayDate(iso: string): string {
  const parsed = parseCalendarDate(iso);
  if (parsed === null) return iso;
  const at = atUtcMidnight(parsed);
  return `${weekdayLongFormatter.format(at)}, ${dateFormatter.format(at)}`;
}

/** `"2026-09-01"` -> `"Sep 1"`. For dense columns where the year is context. */
export function formatCalendarDateShort(iso: string): string {
  const parsed = parseCalendarDate(iso);
  if (parsed === null) return iso;
  return shortDateFormatter.format(atUtcMidnight(parsed));
}

/** Nullable business dates (renewal_date) render as an em dash, never "Invalid Date". */
export function formatOptionalCalendarDate(iso: string | null): string {
  return iso === null ? "—" : formatCalendarDate(iso);
}

/**
 * The UTC calendar day of an RFC 3339 instant.
 *
 * For the fields the contract keeps as timestamps but a person reads as days:
 * a rate's `effective_from`, a binding's window, the day a rate gap opened.
 * The day is taken from the string rather than through a `Date`, because a
 * local-time render would move a midnight-UTC boundary by a day for every
 * reader west of Greenwich — and "the price starts on the 1st" is a fact about
 * the period, not about where the reader sits.
 */
export function formatInstantDate(instant: string): string {
  return formatCalendarDate(instant.slice(0, 10));
}

/** Nullable instants — a period still in force renders as its own word, not a date. */
export function formatOptionalInstantDate(instant: string | null): string {
  return instant === null ? "—" : formatInstantDate(instant);
}

/** `"2026-09"` -> `"Sep 2026"`. The report bucket key. */
export function formatMonth(isoMonth: string): string {
  const match = CALENDAR_MONTH_PATTERN.exec(isoMonth);
  if (match === null) return isoMonth;
  const [, year, month] = match;
  if (year === undefined || month === undefined) return isoMonth;
  return monthFormatter.format(atUtcMidnight({ year: Number(year), month: Number(month), day: 1 }));
}

/**
 * ISO week keys are unreadable on their own, so the billing lineup shows the
 * week's Monday beside it — which the report already supplies as a calendar
 * date precisely so no client has to parse `YYYY-Www`.
 */
export function formatWeek(isoWeek: string, weekStart: string): string {
  return `${isoWeek} · week of ${formatCalendarDateShort(weekStart)}`;
}

/** RFC 3339 instant in the reader's local zone — correct for timestamps only. */
export function formatInstant(instant: string): string {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return instant;
  return instantFormatter.format(parsed);
}

/** ISO dates sort lexicographically, which is the whole point of the format. */
export function compareCalendarDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Today as a calendar date in the reader's own zone. Used only to decide
 * whether an agreement term is in force or still in the future — a question a
 * person asks about THEIR today, not about UTC's.
 */
export function todayIsoDate(now: Date = new Date()): string {
  const year = String(now.getFullYear()).padStart(4, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * A deal's start date is a RANGE (§07 / D5): equal endpoints mean a firm date,
 * a spread means "somewhere in here" and the pipeline report spreads the
 * weighted value across it. Collapsing a range to its earliest date on screen
 * would misrepresent a deal as more certain than the data says it is, so the
 * two render differently.
 */
export function formatDateRange(earliest: string, latest: string): string {
  if (earliest === latest) return formatCalendarDate(earliest);
  return `${formatCalendarDate(earliest)} – ${formatCalendarDate(latest)}`;
}

// ---------------------------------------------------------------------------
// Month arithmetic — the billing calendar's grid
// ---------------------------------------------------------------------------
//
// Everything below is UTC-only arithmetic on plain y/m/d numbers. `Date.UTC`
// plus `getUTC*` is used as a calendar library (it normalises "day 0 of month
// 10" and "day -2 of month 9" correctly, which hand-rolled arithmetic gets
// wrong at leap years), and never as an instant: no `getDate`, no `getMonth`,
// no `toISOString` on a locally-constructed date. That distinction is the whole
// bug class this module exists to prevent — `new Date(2026, 8, 1)` is local
// midnight, and its `toISOString()` is 2026-08-31 for anyone west of UTC, which
// would drop every 1st-of-the-month bill into the wrong cell.

export interface CalendarMonth {
  year: number;
  /** 1-12, the same 1-indexing `CalendarDate` uses. */
  month: number;
}

/** Sunday. A constant rather than a literal so the grid and the weekday
 *  headings can never disagree about which column is which. */
const WEEK_START_DAY = 0;

const DAYS_PER_WEEK = 7;

/** Parse `YYYY-MM`. Returns null for anything else, including month 00 or 13. */
export function parseCalendarMonth(iso: string): CalendarMonth | null {
  const match = CALENDAR_MONTH_PATTERN.exec(iso);
  if (match === null) return null;
  const [, year, month] = match;
  if (year === undefined || month === undefined) return null;
  const parsed = { year: Number(year), month: Number(month) };
  return parsed.month < 1 || parsed.month > 12 ? null : parsed;
}

export function formatIsoMonth(month: CalendarMonth): string {
  return `${String(month.year).padStart(4, "0")}-${String(month.month).padStart(2, "0")}`;
}

export function formatIsoDate(date: CalendarDate): string {
  return `${String(date.year).padStart(4, "0")}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

/** The month a `YYYY-MM-DD` falls in. Null for anything unparseable. */
export function calendarMonthOf(isoDate: string): CalendarMonth | null {
  const parsed = parseCalendarDate(isoDate);
  return parsed === null ? null : { year: parsed.year, month: parsed.month };
}

/** `"2026-09"` -> `"September 2026"`. The calendar's heading, where the short
 *  form the report buckets use would read as an abbreviation of a title. */
export function formatCalendarMonth(month: CalendarMonth): string {
  return longMonthFormatter.format(atUtcMidnight({ ...month, day: 1 }));
}

export function addMonths(month: CalendarMonth, delta: number): CalendarMonth {
  const zeroBased = month.month - 1 + delta;
  return {
    year: month.year + Math.floor(zeroBased / 12),
    // The double modulo is what makes December -> January work backwards as
    // well as forwards: JS `%` keeps the sign of the dividend.
    month: (((zeroBased % 12) + 12) % 12) + 1,
  };
}

/**
 * Step a calendar date by whole days and stay a calendar date: `Date.UTC`
 * normalises month ends and leap years (it is used here as a calendar library,
 * never as an instant), and the result is re-formatted from its UTC components,
 * so no local zone is consulted at any point.
 *
 * An unparseable date comes back unchanged rather than as `Invalid Date` — the
 * caller is a URL param, and the day view has to survive a hand-edited one.
 */
export function addDays(isoDate: string, delta: number): string {
  const parsed = parseCalendarDate(isoDate);
  if (parsed === null) return isoDate;
  const at = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day + delta));
  return formatIsoDate({
    year: at.getUTCFullYear(),
    month: at.getUTCMonth() + 1,
    day: at.getUTCDate(),
  });
}

/**
 * Whether `iso` names a day that EXISTS. `parseCalendarDate` only checks the
 * shape, so `2026-02-30` and `2026-13-01` get through it — and a URL carrying
 * one of those would otherwise become a due-date filter the API rejects. A day
 * that survives `Date.UTC` normalisation unchanged is a real one.
 */
export function isCalendarDate(iso: string): boolean {
  const parsed = parseCalendarDate(iso);
  return parsed !== null && addDays(iso, 0) === iso;
}

export function daysInMonth(month: CalendarMonth): number {
  // Day 0 of the NEXT month is the last day of this one, leap years included.
  return new Date(Date.UTC(month.year, month.month, 0)).getUTCDate();
}

export interface MonthGridDay {
  /** `YYYY-MM-DD`. */
  iso: string;
  /** 1-31, the number the cell prints. */
  day: number;
  /** False for the leading/trailing days borrowed from the neighbouring
   *  months, which the grid still renders (muted) and still queries for. */
  inMonth: boolean;
}

/**
 * Every cell a month's grid renders, in order: the month's own days plus
 * however many days of the neighbouring months it takes to fill whole weeks.
 * The row count is 4-6 depending on the month rather than always 6, so a short
 * February does not draw a blank week.
 */
export function monthGridDays(month: CalendarMonth): readonly MonthGridDay[] {
  const firstWeekday = new Date(Date.UTC(month.year, month.month - 1, 1)).getUTCDay();
  const leading = (firstWeekday - WEEK_START_DAY + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  const cellCount = Math.ceil((leading + daysInMonth(month)) / DAYS_PER_WEEK) * DAYS_PER_WEEK;

  return Array.from({ length: cellCount }, (_unused, index) => {
    const at = new Date(Date.UTC(month.year, month.month - 1, 1 - leading + index));
    const year = at.getUTCFullYear();
    const monthNumber = at.getUTCMonth() + 1;
    const day = at.getUTCDate();
    return {
      iso: formatIsoDate({ year, month: monthNumber, day }),
      day,
      inMonth: year === month.year && monthNumber === month.month,
    };
  });
}

/** The same cells, chunked into the weeks the grid renders as rows. */
export function monthGridWeeks(month: CalendarMonth): ReadonlyArray<readonly MonthGridDay[]> {
  const days = monthGridDays(month);
  const weeks: MonthGridDay[][] = [];
  for (let index = 0; index < days.length; index += DAYS_PER_WEEK) {
    weeks.push(days.slice(index, index + DAYS_PER_WEEK));
  }
  return weeks;
}

/**
 * The inclusive due-date window the grid actually shows — the leading and
 * trailing days included, because a cell that is drawn but not queried is a
 * cell that lies about being empty.
 */
export function monthGridRange(month: CalendarMonth): { from: string; to: string } {
  const days = monthGridDays(month);
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) {
    // Unreachable: every month has at least one week. Stated rather than
    // asserted so this never throws on a hand-edited URL.
    const fallback = formatIsoDate({ ...month, day: 1 });
    return { from: fallback, to: fallback };
  }
  return { from: first.iso, to: last.iso };
}

export interface WeekdayHeading {
  /** The column label, e.g. "Sun". */
  short: string;
  /** The name a screen reader gets instead, e.g. "Sunday". */
  long: string;
}

/**
 * The seven column headings, in the grid's own week order. Derived from a known
 * UTC week (1970-01-04 was a Sunday) rather than hard-coded, so they stay in
 * step with `WEEK_START_DAY` and with the app's locale.
 */
export function weekdayHeadings(): readonly WeekdayHeading[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_unused, index) => {
    const at = new Date(Date.UTC(1970, 0, 4 + WEEK_START_DAY + index));
    return { short: weekdayShortFormatter.format(at), long: weekdayLongFormatter.format(at) };
  });
}

/**
 * How far a bill has moved from where it was first scheduled, in words.
 *
 * `days_shifted` is signed and every screen that shows it wants the same
 * sentence, so the sentence is written once. Deliberately says "late" and
 * "early" rather than "+14" — the number is a fact about a plan somebody
 * committed to, and a signed integer makes the reader do the interpretation.
 *
 * Weeks past a fortnight, because "42 days late" is a number and "6 weeks late"
 * is a fact you can act on. Days below that, because rounding "10 days" to "1
 * week" throws away the half the reader cares about.
 */
export function describeSlip(daysShifted: number): string {
  if (daysShifted === 0) return "on plan";
  const magnitude = Math.abs(daysShifted);
  const direction = daysShifted > 0 ? "late" : "early";
  if (magnitude < 14) {
    return `${magnitude} ${magnitude === 1 ? "day" : "days"} ${direction}`;
  }
  const weeks = Math.round(magnitude / 7);
  return `${weeks} weeks ${direction}`;
}
